import React, { ComponentType, PropsWithChildren, useEffect, useState } from 'react'
import './EditableText.scss'
import { OverflowTooltip } from '../OverflowTooltip/OverflowTooltip'
import { Button } from '../../../../../components/Button/Button'
import { MissingScopeAction, ScopeDescriptor } from '../../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import styled from 'styled-components'

const DefaultViewElement = styled.div``

export const EditableText: React.FC<{
  text: string
  onEdit?: () => void
  onChange?: (text: string) => void | Promise<void>
  onCancel?: () => void
  emptyTextPlaceHolder?: string
  inputPlaceHolder?: string
  textOverflow?: boolean
  rows?: number
  missingScopeAction?: MissingScopeAction
  scope?: ScopeDescriptor
  viewElement?: ComponentType<PropsWithChildren> | null
  editIcon?: string
  hoverIcon?: boolean
}> = ({
  text: initialText,
  onEdit,
  onChange,
  emptyTextPlaceHolder = 'Not provided',
  onCancel,
  inputPlaceHolder = 'Enter text',
  textOverflow = true,
  rows = 4,
  missingScopeAction,
  scope,
  viewElement,
  editIcon = 'bi-pencil-square',
  hoverIcon = false,
}) => {
  const [state, setState] = useState<'view' | 'edit'>('view')
  const [text, setText] = useState<string>(initialText)
  const [editableText, setEditableText] = useState<string>(initialText)
  const [showIcon, setShowIcon] = useState<boolean>(false)

  useEffect(() => {
    setText(initialText)
  }, [initialText])

  const keyDownHandler = async (evt: React.KeyboardEvent<HTMLElement>) => {
    if (evt.key === 'Enter') {
      evt.preventDefault()
      await onTextChangeSaved()
    }
  }

  const onTextChangeSaved = async (e?: React.MouseEvent<HTMLElement>) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    const savedText = text
    setText(editableText)
    setState('view')
    try {
      onChange && (await onChange(editableText))
    } catch (e) {
      setEditableText(savedText)
      setText(savedText)
    }
  }

  const onTextChangeDismissed = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    e.preventDefault()
    setEditableText(text)
    setState('view')
    onCancel && onCancel()
  }

  const handleEdit = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    e.preventDefault()
    setState('edit')
    onEdit && onEdit()
  }

  const ViewElement = viewElement ?? DefaultViewElement

  return (
    <>
      {state === 'view' && (
        <div
          className='d-flex align-items-center gap-2 editable-text__view-container'
          onMouseEnter={() => setShowIcon(true)}
          onMouseLeave={() => setShowIcon(false)}
        >
          {text ? (
            <>
              {textOverflow ? (
                <div className='editable-text__view-text paragraph-large'>
                  <OverflowTooltip>{text}</OverflowTooltip>
                </div>
              ) : (
                <ViewElement className='text-break'>{text}</ViewElement>
              )}
            </>
          ) : (
            <div className='paragraph-medium zocks-text-gray-500'>{emptyTextPlaceHolder}</div>
          )}
          <ScopedBlock missingScopeAction={missingScopeAction ? missingScopeAction : MissingScopeAction.IGNORE} scope={scope}>
            {(!hoverIcon || showIcon) && (
              <Button className='p-0' onClick={handleEdit}>
                <i className={`bi ${editIcon}`}></i>
              </Button>
            )}
          </ScopedBlock>
        </div>
      )}
      {state === 'edit' && (
        <div className='flex-grow-1 d-flex gap-2 align-items-center'>
          <div className='flex-grow-1 d-flex zocks-form-container'>
            <textarea
              rows={rows}
              className='form-control'
              value={editableText}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              onChange={(e) => setEditableText(e.currentTarget.value)}
              onKeyDown={keyDownHandler}
              placeholder={inputPlaceHolder}
            />
          </div>
          <Button variant='primary' className='p-2' onClick={onTextChangeSaved}>
            <i className='bi bi-check-lg'></i>
          </Button>
          <Button variant='secondary' className='p-2' onClick={onTextChangeDismissed}>
            <i className='bi bi-x'></i>
          </Button>
        </div>
      )}
    </>
  )
}
